import { Injectable } from '@angular/core';
import { dateAfterValidator, dateBeforeValidator, DependencyActionEnum, LorenzControlTypeEnum, LorenzFormControl, LorenzFormGroup, LorenzFormList, PermissionEnum, validNumberValidator } from '@lib/forms';
import { ValidatorFn, Validators } from '@angular/forms';
import { RequestOptions } from '@lib/request';
import { AppIdEnum, formatCurrency } from '@lib/shared';

const SERVICE_MITARBEITER_ARTICLE_ID = 'P34';
const STAFFING_DATE_ERROR = 'Bitte geben Sie ein gültiges Datum mit einer Jahreszahl zwischen 1000 und 9999 ein.';

const staffingDateValidator: ValidatorFn = (control) => {
	if (!control.value) {
		return null; // Required validation handles empty inputs.
	}
	const value = String(control.value);
	// Native date inputs can emit 0002/0020/0202 while the year is being typed.
	// The API's holiday calculator (Yasumi) only supports years 1000–9999.
	const date = new Date(`${value}T00:00:00Z`);
	if (!/^[1-9]\d{3}-\d{2}-\d{2}$/.test(value)
		|| Number.isNaN(date.getTime())
		|| date.toISOString().slice(0, 10) !== value) {
		return { staffingDate: true };
	}
	return null;
};

@Injectable({
	providedIn: 'root'
})
export class FormEmployeeService {
	// private readonly libSharedService = inject(LibSharedService);

	formEmployee(options: RequestOptions, functions: { [key: string]: Function }, lorenzFormList: LorenzFormList) {
		// Bestimmung der DEFAULT-Werte
		const einsatztag = getDefault('einsatztag', null, (lorenzFormList.controls ?? []) as LorenzFormGroup[]);
		const schichtstart = getDefault('schichtstart', '08:00', (lorenzFormList.controls ?? []) as LorenzFormGroup[]);
		const schichtende = getDefault('schichtende', '18:00', (lorenzFormList.controls ?? []) as LorenzFormGroup[]);
		const einweisung_vortag_start = getDefault('einweisung_vortag_start', '09:00', (lorenzFormList.controls ?? []) as LorenzFormGroup[]);
		const einweisung_vortag_ende = getDefault('einweisung_vortag_ende', '14:00', (lorenzFormList.controls ?? []) as LorenzFormGroup[]);

		const help = new LorenzFormGroup(
			{
				id: new LorenzFormControl('', [Validators.required], null, {
					order: 1,
					type: LorenzControlTypeEnum.TEXT,
					permissions: {
						backend_request_edit: PermissionEnum.HIDDEN,
						request: PermissionEnum.READ
					}
				}),

				template: new LorenzFormControl('', [], null, {
					type: LorenzControlTypeEnum.HIDDEN
				}),

				funktion: new LorenzFormControl('', [Validators.required], null, {
					order: 2,
					label: 'Funktion',
					type: LorenzControlTypeEnum.SELECT,
					options: options.dialogData?.articles,
					valueChanges: functions['calculatePersonalPositions']
				}),

				geschlecht: new LorenzFormControl('', [Validators.required], null, {
					order: 3,
					label: 'Geschlecht',
					type: LorenzControlTypeEnum.SELECT,
					options: options.dialogData?.genders,
					defaultOption: '0'
				}),

				anzahl: new LorenzFormControl('', [Validators.required], null, {
					order: 4,
					label: 'Anzahl',
					type: LorenzControlTypeEnum.SELECT,
					options: Array.from({ length: 10 }, (_, i) => ({ label: `${i + 1}`, value: `${i + 1}` })),
					defaultOption: '1',
					valueChanges: functions['calculatePersonalPositions']
				}),

				zweite_fremdsprache: new LorenzFormControl('', [Validators.required], null, {
					order: 5,
					label: 'Zweite Fremdsprache',
					type: LorenzControlTypeEnum.TEXT,
					textDefaults: options.dialogData.additionalLanguages,
					valueChanges: functions['calculatePersonalPositions'],
					dependencies: [
						{
							controlName: '.funktion',
							expectedValue: (value: any) => value === 'P17', // Synchronize this value with database! ("Zweite Fremdsprache")
							action: DependencyActionEnum.ENABLE
						}
					]
				}),

				einsatztag: new LorenzFormControl(einsatztag, [Validators.required, staffingDateValidator], null, {
					order: 6,
					label: 'Einsatztag',
					type: LorenzControlTypeEnum.DATE,
					errorMessages: { staffingDate: STAFFING_DATE_ERROR },
					valueChanges: functions['calculatePersonalPositions']
				}),

				schichtstart: new LorenzFormControl(schichtstart, [Validators.required, dateBeforeValidator('.schichtende')], null, {
					order: 7,
					label: 'Schichtstart',
					type: LorenzControlTypeEnum.TIME,
					valueChanges: functions['calculatePersonalPositions']
				}),

				schichtende: new LorenzFormControl(schichtende, [Validators.required, dateAfterValidator('.schichtstart')], null, {
					order: 8,
					label: 'Schichtende',
					type: LorenzControlTypeEnum.TIME,
					valueChanges: functions['calculatePersonalPositions']
				}),

				stunden: new LorenzFormControl('', [], null, {
					order: 9,
					label: 'Stunden',
					type: LorenzControlTypeEnum.OBJECT,
					displayFunction: (value: string) => (value ? `${value} h` : '-'),
					permissions: {
						backend_request_edit: PermissionEnum.READ,
						request: PermissionEnum.READ
					}
				}),

				stundensatz: new LorenzFormControl('', [validNumberValidator()], null, {
					order: 9.5,
					label: 'Stundensatz',
					type: LorenzControlTypeEnum.TEXT,
					placeholder: '34,90',
					valueChanges: functions['calculatePersonalPositions'],
					permissions: {
						backend_request_edit: PermissionEnum.FULL,
						request: PermissionEnum.HIDDEN
					},
					dependencies: [
						{
							controlName: '.funktion',
							expectedValue: (value: any) => value === SERVICE_MITARBEITER_ARTICLE_ID,
							action: DependencyActionEnum.ENABLE
						}
					]
				}),

				preis: new LorenzFormControl('', [], null, {
					order: 10,
					label: 'Preis',
					type: LorenzControlTypeEnum.OBJECT,
					displayFunction: (value: string) => (value ? formatCurrency(value) : '-'),
					permissions: {
						backend_request_edit: PermissionEnum.READ,
						request: PermissionEnum.READ
					}
				}),

				buchungszeit_hinweis: new LorenzFormControl(false, [], null, {
					order: 11,
					type: LorenzControlTypeEnum.HTML,
					innerHTML: 'Mindestbuchungsdauer beträgt 5h!',
					disableValueBuffer: true,
					dependencies: [
						{
							controlName: '.buchungszeit_hinweis',
							expectedValue: (value: any) => !!value,
							action: DependencyActionEnum.ENABLE
						}
					]
				}),

				aufgabengebiet: new LorenzFormControl('', [], null, {
					order: 12,
					label: 'Aufgabengebiet',
					type: LorenzControlTypeEnum.TEXTAREA,
					placeholder: 'Bitte beschreiben Sie hier das Aufgabengebiet / Tätigkeiten des angeforderten Personals.'
				}),

				einweisung: new LorenzFormControl('', [], null, {
					order: 13,
					label: 'Einweisung',
					type: LorenzControlTypeEnum.RADIO,
					options: [
						{ label: 'Keine Einweisung', value: 'keine' },
						{ label: 'Einweisung am Einsatztag', value: 'einsatztag' },
						{ label: 'Einweisung am Vortag', value: 'vortag' }
					],
					defaultOption: 'keine',
					valueChanges: functions['calculatePersonalPositions']
				}),

				einweisung_vortag_text: new LorenzFormControl('', [], null, {
					order: 14,
					label: 'Geben Sie bitte die Uhrzeiten für die Einweisung am Vortag an',
					direction: 'row',
					type: LorenzControlTypeEnum.OBJECT,
					dependencies: [
						{
							controlName: '.einweisung',
							expectedValue: (value: any) => value === 'vortag',
							action: DependencyActionEnum.ENABLE
						}
					]
				}),

				einweisung_vortag_start: new LorenzFormControl(einweisung_vortag_start, [Validators.required], null, {
					order: 15,
					label: 'von',
					type: LorenzControlTypeEnum.TIME,
					dependencies: [
						{
							controlName: '.einweisung',
							expectedValue: (value: any) => value === 'vortag',
							action: DependencyActionEnum.ENABLE
						}
					],
					valueChanges: functions['calculatePersonalPositions']
				}),

				einweisung_vortag_ende: new LorenzFormControl(einweisung_vortag_ende, [Validators.required], null, {
					order: 16,
					label: 'bis',
					type: LorenzControlTypeEnum.TIME,
					dependencies: [
						{
							controlName: '.einweisung',
							expectedValue: (value: any) => value === 'vortag',
							action: DependencyActionEnum.ENABLE
						}
					],
					valueChanges: functions['calculatePersonalPositions']
				}),

				einweisung_vortag_stunden: new LorenzFormControl('', [], null, {
					order: 17,
					label: 'Stunden Einarbeitung',
					type: LorenzControlTypeEnum.OBJECT,
					displayFunction: (value: string) => (value ? `${value} h` : '-'),
					permissions: {
						backend_request_edit: PermissionEnum.READ,
						request: PermissionEnum.READ
					},
					dependencies: [
						{
							controlName: '.einweisung',
							expectedValue: (value: any) => value === 'vortag',
							action: DependencyActionEnum.ENABLE
						}
					]
				}),

				einweisung_vortag_stunden_marker: new LorenzFormControl(false, [], null, {
					type: LorenzControlTypeEnum.HIDDEN
				}),

				einweisung_vortag_preis: new LorenzFormControl('', [], null, {
					order: 18,
					label: 'Preis Einarbeitung',
					type: LorenzControlTypeEnum.OBJECT,
					displayFunction: (value: string) => (value ? formatCurrency(value) : '-'),
					permissions: {
						backend_request_edit: PermissionEnum.READ,
						request: PermissionEnum.READ
					},
					dependencies: [
						{
							controlName: '.einweisung',
							expectedValue: (value: any) => value === 'vortag',
							action: DependencyActionEnum.ENABLE
						}
					]
				}),

				einweisung_vortag_buchungszeit_hinweis: new LorenzFormControl(false, [], null, {
					order: 19,
					type: LorenzControlTypeEnum.HTML,
					innerHTML: 'Mindestbuchungsdauer beträgt 5h!',
					dependencies: [
						{
							controlName: '.einweisung_vortag_buchungszeit_hinweis',
							expectedValue: (value: any) => !!value,
							action: DependencyActionEnum.ENABLE
						}
					]
				})
			},
			null,
			null,
			{
				label: '',
				hasSaveButton: [AppIdEnum.BACKEND_REQUEST_EDIT].includes(options.appId),
				permissions: {
					backend: PermissionEnum.FULL,
					request: PermissionEnum.FULL
				}
			}
		);

		return help;
	}
}

// ----- HELPER FUNCTIONS ----------------------------------------------------------------------------------------------

const getDefault = (lorenzFormControlName: string, defaultValue: any, lorenzFormListControls: LorenzFormGroup[]): string => {
	if (lorenzFormListControls.length === 0) {
		return defaultValue;
	}
	const currentValue = lorenzFormListControls[lorenzFormListControls.length - 1].get(lorenzFormControlName)?.value;

	return currentValue ?? defaultValue;
};
